import { useSyncExternalStore } from "react";

/**
 * Store nhỏ, sống ngoài React — thay cho Redux ở phần state THẬT SỰ dùng chung
 * (người đăng nhập, menu). Không reducer, không action type, không middleware.
 *
 * Vì sao phải ngoài React: interceptor 401 trong `utils/config.js` cần xóa phiên
 * mà nó không nằm trong cây component. Trước đây việc này phải đi qua
 * `persistor.purge()`; nay gọi thẳng `store.set()`.
 *
 * Vì sao không giữ Redux cho phần này: chỉ có hai mẩu state là dùng chung thật,
 * còn lại là dữ liệu của từng màn — thứ `useAsync` lo gọn hơn. 46 slice + persist
 * chỉ để chở hai mẩu đó là không đáng.
 *
 * ĐỌC KỸ về `use(selector)`: `useSyncExternalStore` đòi snapshot phải ổn định theo
 * `Object.is` giữa các lần render, nên selector **chỉ được** trả về primitive hoặc
 * chính một nhánh có sẵn trong state. Trả về object/array dựng mới mỗi lần
 * (`s => ({ a: s.a })`, `s => s.list.filter(...)`) sẽ render vô hạn. Cần dẫn xuất
 * thì lấy cả state rồi `useMemo` ở component.
 *
 * @template T
 * @param {T} initialState
 * @param {{ persistKey?: string, pick?: (state: T) => Partial<T> }} [options]
 *   persistKey: có thì đọc/ghi localStorage. Đọc **đồng bộ lúc nạp module** nên
 *   state đã sẵn sàng trước lần render đầu — khác redux-persist rehydrate bất đồng
 *   bộ (dự án lại không bọc `PersistGate`, nên màn đầu luôn thấy state rỗng rồi mới
 *   nhảy; đó là lý do các màn phải tự dựng cờ `hasFetchedData`).
 *   pick: chọn nhánh nào được ghi xuống đĩa (mặc định ghi cả state).
 */
export function createStore(initialState, options = {}) {
  const { persistKey, pick } = options;

  let state = persistKey ? { ...initialState, ...readPersisted(persistKey) } : initialState;
  const listeners = new Set();

  const get = () => state;

  const set = (next) => {
    const patch = typeof next === "function" ? next(state) : next;
    const value = { ...state, ...patch };
    if (shallowEqual(value, state)) return;
    state = value;
    if (persistKey) writePersisted(persistKey, pick ? pick(state) : state);
    listeners.forEach((fn) => fn());
  };

  /** Đưa state về ban đầu và xóa bản lưu trên đĩa. */
  const reset = () => {
    state = initialState;
    if (persistKey) removePersisted(persistKey);
    listeners.forEach((fn) => fn());
  };

  const subscribe = (fn) => {
    listeners.add(fn);
    return () => listeners.delete(fn);
  };

  /**
   * Hook đọc state trong component. Không truyền selector = lấy cả state.
   * Tên phải bắt đầu bằng `use` để eslint `react-hooks/rules-of-hooks` nhận đây là
   * custom hook (nếu đặt tên `use` không thôi thì nó báo gọi hook ngoài component).
   */
  function useStore(selector) {
    const snapshot = selector ? () => selector(state) : get;
    return useSyncExternalStore(subscribe, snapshot, snapshot);
  }

  return { get, set, reset, subscribe, use: useStore };
}

/* --------------------------------- Nội bộ ---------------------------------- */

function shallowEqual(a, b) {
  if (Object.is(a, b)) return true;
  const ka = Object.keys(a);
  if (ka.length !== Object.keys(b).length) return false;
  return ka.every((k) => Object.is(a[k], b[k]));
}

function readPersisted(key) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    // Trình duyệt chặn storage (chế độ riêng tư) hoặc JSON hỏng — chạy tiếp với
    // state khởi tạo, không để cả app chết vì một mẩu cache.
    return null;
  }
}

function writePersisted(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* bỏ qua: vẫn giữ trong RAM */
  }
}

function removePersisted(key) {
  try {
    localStorage.removeItem(key);
  } catch {
    /* bỏ qua */
  }
}
